import React, { useState, useEffect, useRef } from 'react';
import { Navigate } from 'react-router-dom';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { TrendingUp, TrendingDown, FileText, Archive, Star, AlertTriangle, RefreshCw, Download, Activity } from 'lucide-react';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import html2canvas from 'html2canvas';

import Sidebar from '../sidebar/Sidebar';
import apiService from '../../utils/api';
import Loader from '../Loader';
import AnalyticsDashboard from '../Analytics/AnalyticsDashboard';
import './Dashboard.css';


const AdminDashboard = () => {
  const dashboardRef = useRef(null);
  const [showDownloadMenu, setShowDownloadMenu] = useState(false);
  const [loading, setLoading] = useState(true);
  const [filterPeriod, setFilterPeriod] = useState('30d');
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [stats, setStats] = useState({
    totalContracts: 0,
    activeContracts: 0,
    archivedContracts: 0,
    favoritesCount: 0,
    contractTypes: [],
    statusDistribution: [],
    creationTrends: [],
    riskFlags: [],
    userActivity: [],
    creationMethods: [],
    monthlyTrends: [],
    growthRate: 0,
    avgProcessingTime: 0,
    complianceScore: 0,
  });
  // Pagination state for risk table
  const [riskPage, setRiskPage] = useState(1);
  const riskRowsPerPage = 10;
  const statusColorMap = {
    "Active": "#1976D2",
    "Approved": "#4ec454ff",
    "Archived": "#9C27B0",
    "Draft": "#ec407a",
    "Expired": "#C62828",
    "Pending Approval": "#f59e0b",
    "Signed": "#009688",
    "Terminated": "#020406ff",
    "Trashed": "#E64A19",
    "Moved to Trash": "#E64A19",
    "Service Agreement": "#f59e0b",
    "Consulting Agreement": "#10b981",
    "Data Agreement": "#6366f1",
    "Partnership Agreement": "#ef4444",
    "Amendment": "#8b5cf6",
    "Requisition Document": "#3b82f6",
    "Contractor Agreement": "#ec4899",
    "NDA": "#6366f1",
    "Non-Disclosure Agreement(NDA)": "#6366f1",
    "Master Services Agreement(MSA)": "#3b82f6",
    "Statement of Work(SOW)": "#8b5cf6",
    "Change Request(CR)": "#ec4899",
    "Purchase Order(PO)": "#f59e0b",
    "Non-Disclosure Agreement": "#6366f1",
    "Vendor Agreement": "#10b981",
    "Addendum SOW": "#ef4444",
  };

  const getColor = (name, fallback = "#9e9e9e") => statusColorMap[name] || fallback;


  useEffect(() => {
    fetchDashboardData();
  }, [filterPeriod]);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      // Pass filterPeriod as a query param to backend APIs
      const mainData = await apiService.fetchData(`dashboard?period=${filterPeriod}`);
      const riskData = await apiService.fetchData(`risk?period=${filterPeriod}`);
      const transformedCreationMethods = mainData.creationMethods.map(item => ({
        ...item,
        method: item.method.charAt(0).toUpperCase() + item.method.slice(1)
      }));

      setStats({
        totalContracts: mainData.totalContracts || 0,
        activeContracts: mainData.activeContracts || 0,
        archivedContracts: mainData.archivedContracts || 0,
        favoritesCount: mainData.favoritesCount || 0,
        contractTypes: mainData.contractTypes || [],
        statusDistribution: mainData.statusDistribution || [],
        creationTrends: mainData.creationTrends || [],
        riskFlags: riskData.riskFlags || [],
        userActivity: mainData.userActivity || [],
        creationMethods: transformedCreationMethods,
        monthlyTrends: mainData.monthlyTrends || [],
        growthRate: mainData.growthRate || 0,
        avgProcessingTime: mainData.avgProcessingTime || 0,
        complianceScore: mainData.complianceScore || 0,
      });
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
      // Handle error, e.g., set error state
    } finally {
      setLoading(false);
    }
  };

  const getUserRole = () => (sessionStorage.getItem('user_role') || '').replace(/\s+/g, '');
  if (getUserRole() !== 'Admin') {
    return <Navigate to="/unauthorized" />;
  }

  const exportToExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(stats.riskFlags);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Risk Flags');
    XLSX.writeFile(workbook, 'clm-risk-flags.xlsx');
  };

  // Export entire dashboard (stats, charts, table) to PDF
  const exportToPDF = async () => {
    const dashboardElem = dashboardRef.current;
    if (!dashboardElem) return;
    // Use html2canvas to capture dashboard as image
    const canvas = await html2canvas(dashboardElem, { scale: 2 });
    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF({ orientation: 'landscape', unit: 'px', format: [canvas.width, canvas.height + 400] });
    pdf.addImage(imgData, 'PNG', 0, 0, canvas.width, canvas.height);
    // Add table data below the image
    const tableY = canvas.height + 20;
    const tableHead = [['Contract Title', 'Risk Level', 'Created Date', 'Department', 'Days Pending', 'Renewal Date']];
    const tableBody = stats.riskFlags.map(risk => [
      risk.contract_title,
      risk.risk,
      risk.created_date ? new Date(risk.created_date).toLocaleDateString() : '-',
      risk.department,
      `${risk.days} days`,
      risk.renewal_date ? new Date(risk.renewal_date).toLocaleDateString() : '-'
    ]);
    autoTable(pdf, {
      head: tableHead,
      body: tableBody,
      startY: tableY,
      theme: 'grid',
      styles: { fontSize: 12 },
      headStyles: { fillColor: [255, 107, 53] },
    });
    pdf.save('clm-dashboard-report.pdf');
  };

  const StatCard = ({ title, value, change, icon: Icon, trend, color }) => (
    <div className="dashboard-stat-card" style={{ borderTopColor: color }}>
      <div className="dashboard-stat-header">
        <div className="dashboard-stat-icon" style={{ background: `${color}15`, color: color }}>
          <Icon size={24} />
        </div>
        <div className={`dashboard-stat-trend ${trend === 'up' ? 'dashboard-trend-up' : 'dashboard-trend-down'}`}>
          {trend === 'up' ? <TrendingUp size={16} /> : <TrendingDown size={16} />}
          {change}%
        </div>
      </div>
      <h3 className="dashboard-stat-title">{title}</h3>
      <div className="dashboard-stat-value">{value}</div>
    </div>
  );

  const getPaginationButtonStyle = (isDisabled) => ({
    background: isDisabled ? '#fff' : '#ff6b35',
    color: isDisabled ? '#ff6b35' : '#fff',
    border: '1px solid #ff6b35',
    borderRadius: '10px',
    padding: '6px 18px',
    fontWeight: 'bold',
    cursor: isDisabled ? 'not-allowed' : 'pointer',
    minWidth: '80px',
    transition: 'all 0.2s',
  });

  const isFirstPage = () => riskPage === 1;
  const isLastPage = () => riskPage >= Math.ceil(stats.riskFlags.length / riskRowsPerPage);
  const getTotalPages = () => Math.max(1, Math.ceil(stats.riskFlags.length / riskRowsPerPage));

  if (loading) {
    return <Loader isLoading={true} message="Loading Dashboard..." color="#ff6b35" />;
  }

  return (
    <div className="dashboard-page-container">
      <Sidebar />
      <div className="dashboard-modern-dashboard" ref={dashboardRef}>
        {/* Header */}
        <header className="dashboard-header-modern">
          <div className="dashboard-header-content">
            <div>
              <h1 className="dashboard-main-title">Contract Lifecycle Management</h1>
              <p className="dashboard-subtitle">Real-time analytics and insights</p>
            </div>
            <div className="dashboard-header-actions">
              <select
                className="dashboard-period-selector"
                value={filterPeriod}
                onChange={(e) => setFilterPeriod(e.target.value)}
                title="Filter data by time period"
              >
                <option value="7d">Last 7 Days</option>
                <option value="30d">Last 30 Days</option>
                <option value="90d">Last 90 Days</option>
                <option value="1y">Last Year</option>
              </select>
              <button className="dashboard-action-btn dashboard-refresh-btn" onClick={fetchDashboardData} title="Refresh dashboard data">
                <RefreshCw size={18} />
                Refresh
              </button>
              <button 
                className="dashboard-action-btn" 
                onClick={() => setShowAnalytics(!showAnalytics)}
                style={{ background: showAnalytics ? '#10b981' : '#ff6b35' }}
                title="Toggle Analytics Dashboard"
              >
                {showAnalytics ? 'Overview' : 'Analytics Dashboard'}
              </button>
              <button
                className="dashboard-action-btn dashboard-export-btn"
                onClick={() => setShowDownloadMenu((v) => !v)}
                title="Export data to Excel and PDF"
                style={{ position: 'relative' }}
              >
                <Download size={18} />
                Export
                {showDownloadMenu && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    right: 0,
                    background: '#fff',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
                    zIndex: 100,
                    minWidth: '180px',
                  }}>
                    <button
                      style={{
                        width: '100%',
                        padding: '10px 16px',
                        background: 'none',
                        border: 'none',
                        color: '#ff6b35',
                        fontWeight: 600,
                        textAlign: 'left',
                        cursor: 'pointer',
                        borderBottom: '1px solid #e5e7eb',
                      }}
                      onClick={() => {
                        exportToExcel();
                        setShowDownloadMenu(false);
                      }}
                    >
                      Download Table (Excel)
                    </button>
                    <button
                      style={{
                        width: '100%',
                        padding: '10px 16px',
                        background: 'none',
                        border: 'none',
                        color: '#ff6b35',
                        fontWeight: 600,
                        textAlign: 'left',
                        cursor: 'pointer',
                      }}
                      onClick={async () => {
                        await exportToPDF();
                        setShowDownloadMenu(false);
                      }}
                    >
                      Download Full Report (PDF)
                    </button>
                  </div>
                )}
              </button>
            </div>
          </div>
        </header>

        {showAnalytics ? (
          <AnalyticsDashboard />
        ) : (
          <>
        {/* Stats Grid */}
        <div className="dashboard-stats-grid">
          <StatCard
            title="Total Contracts"
            value={stats.totalContracts}
            change={12.5}
            icon={FileText}
            trend="up"
            color="#3b82f6"
          />
          <StatCard
            title="Active Contracts"
            value={stats.activeContracts}
            change={8.3}
            icon={Activity}
            trend="up"
            color="#3b82f6"
          />
          <StatCard
            title="Archived"
            value={stats.archivedContracts}
            change={-3.2}
            icon={Archive}
            trend="down"
            color="#6b7280"
          />
          <StatCard
            title="Favorites"
            value={stats.favoritesCount}
            change={15.7}
            icon={Star}
            trend="up"
            color="#f59e0b"
          />
        </div>

        {/* Main Charts Section */}
        <div className="dashboard-charts-section">
          {/* <div className="dashboard-chart-container dashboard-large">
            <div className="dashboard-chart-header">
              <div>
                <h3>Contract Creation & Review Trends</h3>
                <p>Daily activity over the last 30 days</p>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={320}>
              <AreaChart data={stats.creationTrends}>
                <defs>
                  <linearGradient id="colorContracts" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="colorReviews" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="date" stroke="#6b7280" fontSize={12} />
                <YAxis stroke="#6b7280" fontSize={12} />
                <Tooltip
                  contentStyle={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px' }}
                  formatter={(value, name) => [`${value}`, name]}
                />
                <Legend />
                <Area
                  type="monotone"
                  dataKey="contracts"
                  stroke="#3b82f6"
                  strokeWidth={2}
                  fillOpacity={1}
                  fill="url(#colorContracts)"
                  name="Contracts Created"
                />
                <Area
                  type="monotone"
                  dataKey="reviews"
                  stroke="#8b5cf6"
                  strokeWidth={2}
                  fillOpacity={1}
                  fill="url(#colorReviews)"
                  name="Reviews Completed"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div> */}

          <div className="dashboard-chart-container dashboard-small-chart">
            <div className="dashboard-chart-header">
              <div>
                <h3>Contract Types</h3>
                <p>Distribution by type</p>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={400}>
              <PieChart>
                <Pie
                  data={stats.contractTypes}
                  cx="50%"
                  cy="50%"
                  labelLine={true}
                  label={({ name, percent }) => percent > 0.05 ? `${name} ${(percent * 100).toFixed(0)}%` : ''}
                  outerRadius={100}
                  innerRadius={60}
                  paddingAngle={5}
                  dataKey="value"
                >
                  {stats.contractTypes.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={getColor(entry.name, entry.color)} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px' }}
                  formatter={(value, name) => [`${value}`, name]}
                />
                <Legend
                  layout="horizontal"
                  verticalAlign="bottom"
                  align="center"
                  wrapperStyle={{ paddingTop: '20px', fontSize: '12px' }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>

          <div className="dashboard-chart-container dashboard-small-chart">
            <div className="dashboard-chart-header">
              <div>
                <h3>Status Overview</h3>
                <p>Current contract status</p>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={stats.statusDistribution} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis type="number" stroke="#6b7280" fontSize={12} />
                <YAxis dataKey="name" type="category" stroke="#6b7280" fontSize={12} width={80} />
                <Tooltip
                  contentStyle={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px' }}
                  formatter={(value, name) => [`${value}`, name]}
                />
                <Bar dataKey="value" radius={[0, 8, 8, 0]}>
                  {stats.statusDistribution.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={getColor(entry.name, entry.color)} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* User Activity & Creation Methods */}
        <div className="dashboard-charts-section">
          <div className="dashboard-chart-container">
            <div className="dashboard-chart-header">
              <div>
                <h3>User Performance</h3>
                <p>Contracts created by each role</p>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={340}>
              <PieChart>
                <Pie
                  data={stats.userActivity}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="contracts_created"
                  nameKey="role"
                  label={({ role, percent }) => `${role} ${(percent * 100).toFixed(0)}%`}
                >
                  {stats.userActivity.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color || ['#3b82f6', '#8b5cf6', '#f59e0b', '#ec4899'][index]} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px' }}
                  formatter={(value, name, props) => [`${value} contracts`, props.payload.role]}
                />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>

          <div className="dashboard-chart-container">
            <div className="dashboard-chart-header">
              <div>
                <h3>Creation Methods</h3>
                <p>How contracts are created</p>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={340}>
              <BarChart data={stats.creationMethods}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="method" stroke="#6b7280" fontSize={12} />
                <YAxis stroke="#6b7280" fontSize={12} />
                <Tooltip
                  contentStyle={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px' }}
                  formatter={(value, name) => [`${value}`, name]}
                />
                <Bar dataKey="count" fill="#3b82f6" radius={[8, 8, 0, 0]}>
                  {stats.creationMethods.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={['#3b82f6', '#8b5cf6', '#ec4899', '#f59e0b'][index]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Risk Table */}
        <div className="dashboard-risk-section">
          <div className="dashboard-section-header">
            <div>
              <h2>Risk & Compliance Insights</h2>
              <p>Contracts requiring immediate attention</p>
            </div>
            {/* <div className="dashboard-risk-filters">
              <button className="dashboard-filter-btn dashboard-active">All</button>
              <button className="dashboard-filter-btn">High</button>
              <button className="dashboard-filter-btn">Medium</button>
              <button className="dashboard-filter-btn">Low</button>
            </div> */}
          </div>
          <div className="dashboard-risk-table-container">
            <table className="dashboard-modern-table">
              <thead>
                <tr>
                  <th>Contract Title</th>
                  <th>Risk Level</th>
                  <th>Created Date</th>
                  <th>Department</th>
                  <th>Days Pending</th>
                  <th>Renewal Date</th>
                </tr>
              </thead>
              <tbody>
                {stats.riskFlags
                  .slice((riskPage - 1) * riskRowsPerPage, riskPage * riskRowsPerPage)
                  .map((risk) => (
                    <tr key={risk.id}>
                      <td className="dashboard-contract-title">{risk.contract_title}</td>
                      <td>
                        <span className={`dashboard-risk-badge dashboard-risk-${risk.risk?.toLowerCase()}`}>{risk.risk === 'High' && <AlertTriangle size={14} />} {risk.risk}</span>
                      </td>
                      <td>{risk.created_date ? new Date(risk.created_date).toLocaleDateString() : '-'}</td>
                      <td><span className="dashboard-department-tag">{risk.department}</span></td>
                      <td><span className="dashboard-days-badge">{risk.days} days</span></td>
                      <td>{risk.renewal_date ? new Date(risk.renewal_date).toLocaleDateString() : '-'}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
            {/* Pagination Controls - styled like TreeTableSample */}
            <div className="dashboard-table-pagination" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginTop: '18px', gap: '10px' }}>
              <button
                className="dashboard-pagination-btn"
                style={getPaginationButtonStyle(isFirstPage())}
                onClick={() => setRiskPage((prev) => Math.max(prev - 1, 1))}
                disabled={isFirstPage()}
              >
                Previous
              </button>
              <span className="dashboard-pagination-info" style={{ fontWeight: 'bold', color: '#ff6b35', fontSize: '16px' }}>
                Page {riskPage} of {getTotalPages()}
              </span>
              <button
                className="dashboard-pagination-btn"
                style={getPaginationButtonStyle(isLastPage())}
                onClick={() => setRiskPage((prev) =>
                  prev < getTotalPages() ? prev + 1 : prev
                )}
                disabled={isLastPage()}
              >
                Next
              </button>
            </div>
          </div>
        </div>
        </>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;